import { CheckIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';

interface WizardStepperProps {
  className?: string;
  steps: string[];
  /** Zero-based index of the step being shown. */
  current: number;
  /**
   * Whether every step is reachable, not just the ones already completed.
   *
   * True when EDITING: the product already holds a valid record in all three
   * steps, so there is nothing to unlock by walking forward and the merchant
   * who opened the wizard to change one modifier should not have to click
   * «Продолжить» twice to get there. False on a CREATE, where the later steps
   * have nothing to show yet.
   */
  allStepsReachable?: boolean | undefined;
  /** Jump to a reachable step. */
  onStepSelect: (index: number) => void;
}

/**
 * A wizard's progress header (Figma 840-17545 / 1112-19909): numbered circles
 * joined by a rule, each labelled, with completed steps collapsing to a filled
 * check.
 *
 * Shared by the product wizard and the promotion wizard, which is why it lives
 * here rather than in either feature — FSD forbids one feature importing
 * another, and both draw the identical control.
 *
 * Which symbols are BUTTONS depends on the mode. Going back is always safe —
 * the form holds every step's values at once — so a completed step is always
 * clickable. Jumping FORWARD is gated on `allStepsReachable`, because on a
 * create it would skip the per-step validation the caller runs on «Продолжить»
 * and land the merchant on the last step with an invalid first one.
 */
export function WizardStepper({
  className,
  steps,
  current,
  allStepsReachable = false,
  onStepSelect,
}: WizardStepperProps) {
  return (
    <ol className={cn('flex items-start justify-center gap-0', className)}>
      {steps.map((label, index) => {
        const isDone = index < current;
        const isCurrent = index === current;
        const isLast = index === steps.length - 1;
        // The current step is deliberately not a button — it navigates nowhere.
        const isSelectable = !isCurrent && (isDone || allStepsReachable);

        // The whole column — circle AND label — is one target. A 32px circle
        // is a small thing to hit, and the label reads as part of the same
        // control, so clicking it doing nothing is a papercut. Rendered as a
        // plain div when the step is not reachable, so a locked step is not
        // focusable and announces nothing to click.
        const Column = isSelectable ? 'button' : 'div';

        return (
          <li key={label} className="flex items-start">
            <Column
              {...(isSelectable
                ? {
                    type: 'button' as const,
                    onClick: () => onStepSelect(index),
                    'aria-label': label,
                  }
                : {})}
              className={cn(
                'flex w-[7.5rem] flex-col items-center gap-2.5 rounded-xl outline-none',
                isSelectable &&
                  'cursor-pointer focus-visible:ring-[3px] focus-visible:ring-ring/40',
              )}
            >
              <StepSymbol index={index} isDone={isDone} isCurrent={isCurrent} />
              <span
                className={cn(
                  'text-center text-sm font-medium tracking-tight',
                  isCurrent || isDone ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {label}
              </span>
            </Column>
            {/* The connector sits between symbols, so it hangs off every step
             * but the last and is nudged up to meet the circles' centre. */}
            {!isLast && (
              <span
                aria-hidden
                className={cn(
                  'mt-4 -mr-10 -ml-10 h-0.5 w-20 rounded-full',
                  isDone ? 'bg-primary' : 'bg-border',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

interface StepSymbolProps {
  index: number;
  isDone: boolean;
  isCurrent: boolean;
}

/**
 * The circle: a check when complete, otherwise the 1-based step number.
 *
 * Presentational only — the enclosing column owns the click, the focus ring and
 * the pointer cursor, so the whole circle+label pair is one target rather than
 * just the 32px disc.
 */
function StepSymbol({ index, isDone, isCurrent }: StepSymbolProps) {
  // Zero-padded to match the design's "01 / 02 / 03".
  const number = String(index + 1).padStart(2, '0');

  return (
    <span
      aria-current={isCurrent ? 'step' : undefined}
      className={cn(
        'flex size-8 items-center justify-center rounded-full border-2 text-sm font-medium tracking-tight',
        isDone && 'border-primary bg-primary text-primary-foreground',
        isCurrent && 'border-primary bg-card text-foreground',
        !isDone && !isCurrent && 'border-border bg-card text-muted-foreground',
      )}
    >
      {isDone ? <CheckIcon className="size-4" aria-hidden /> : number}
    </span>
  );
}
